import { AIClient } from "@/ai/AIAssistant";
import { AppBarData } from "@/appBar/appBarDomain";
import {
  Failure,
  failure,
  isFailure,
  isOk,
  isPending,
  mapToSigners,
  Ok,
  ok,
  Signer,
  signer,
} from "@/common/common";
import {
  BooleanFieldModel,
  RadioFieldModel,
  SignersForm,
  SigningSettings,
  StringFieldModel,
} from "@/common/commonForms";
import { CustomRoleForUser } from "@/common/customRole";
import { ModuleName } from "@/common/product";
import { ButtonProps, LinkButtonProps } from "@/components-v2/Button";
import {
  DropdownMenuItems,
  DropdownMenuItemType,
  DropdownMenuProps,
} from "@/components-v2/Dropdown";
import { FilePreviewProps, PreviewRendition } from "@/components-v2/FilePreview";
import { IconProps } from "@/components-v2/Icon";
import { IconName } from "@/components-v2/iconMapping";
import { SelectWithNavigatorProps } from "@/components-v2/Select";
import { SelectFilesButtonProps } from "@/components-v2/SelectFilesButton";
import { TabProps } from "@/components-v2/Tabs";
import {
  ResendDocumentForSigningData,
  ViewDocumentSigningStatusDialogModel,
} from "@/dbot/summary/summaryDomain";
import { DocumentSigningStatus, GetDocumentSigningStatusResponse } from "@/document/documentApi";
import {
  actionName,
  confirmAction,
  DCEvent,
  EventDispatcher,
  httpDelete,
  HttpEvent,
  HttpEventCallbacks,
  httpGet,
  httpPost,
  HttpResponse,
  loadUrl,
  navigate,
  NavigateEvent,
  SetTimeoutEvent,
  showError,
  showGeneralError,
  showGeneralSuccess,
  showSuccess,
  thunk,
  updateStore,
  UpdateStoreEvent,
  WithStoreEvent,
} from "@/events/eventsDomain";
import { RootStoreData } from "@/events/rootStoreData";
import { ConversionModel, supportedFiletypesForConversion } from "@/fileUpload/ConversionModel.ts";
import {
  emptyFileUploadConfig,
  FileUploadConfig,
  isFileUploadValidationError,
} from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage } from "@/i18n/tt";
import { MeetingType } from "@/meetings/meetingsDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { CompanyInfo, CompanyRole } from "@/types/CompanyInfo";
import {
  granteeLabel,
  PermissionPrivilege,
  permissionPrivilegeLabel,
  privilegeRank,
} from "@/types/Permissions";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { User } from "@/types/UserInfo";
import { next, prev } from "@/utils/arrays";
import { getExtension, isExtensionPdf } from "@/utils/files";
import { orderlessPredicate } from "@/utils/filter";
import { when, withValue } from "@/utils/functions";
import { isArrowDown, isArrowUp, isTargetInput } from "@/utils/keyboardEvents";
import {
  adminArchive,
  adminProjects,
  navigateToProjectEvent,
  previewBoardMeetingForAdmin,
} from "@/utils/navigation";
import { fromQueryString, singleValue, toQueryString } from "@/utils/queryParams";
import { getFullName } from "@/utils/Roles";
import { isBlank } from "@/utils/strings.ts";
import { toArchiveReference } from "@/utils/UrnReference";
import { companySettingsApi } from "@/views/CompanySettings/CompanySettingsApi";
import { AxiosInstance } from "axios";
import { compact, groupBy, identity, isFunction, last, maxBy, omit } from "lodash";
import { isEqual, sortBy, uniqWith } from "lodash/";
import isEmpty from "lodash/isEmpty";
import { reaction, toJS, when as mobxWhen } from "mobx";
import {
  ArchiveDocumentStatus,
  ArchiveDocumentType,
  ArchiveEntryAction,
  ArchivePathNode,
  ArchiveRootFolderType,
  ArchiveRootFolderView,
  getActionIcon,
  getActionLabel,
  isArchiveRootFolderView,
} from "./commonArchiveDomain";
import { hasPreviewRendition, PREVIEW_RENDITION_POLL_INTERVAL_MS } from "./previewRenditionDomain";

function isRootFolderType(s?: string): s is ArchiveRootFolderType {
  return s !== undefined && (Object.values(ArchiveRootFolderType) as string[]).includes(s);
}

export type ArchiveEntry = {
  id: string;
  rootId?: string;
  parentId?: string;
  type: ArchiveDocumentType;
  name: string;
  updated?: string;
  signingStatus?: DocumentSigningStatus;
  isFolder?: boolean;
  isReadonly?: boolean;
  projectId?: string;
  meetingType?: MeetingType;
  meetingDate?: string;
  linkTargetType?: ArchiveDocumentType;
  linkTargetId?: string;
  folderDescription?: string;
  folderIcon?: IconName;
  folderIconColor?: string;
  children?: ArchiveEntry[];
  isStarred?: boolean;
};

export const isSigningInProgress = (entry: ArchiveEntry): boolean =>
  entry.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
  entry.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS;

export type ArchiveRootFolder = {
  folderType: ArchiveRootFolderType;
  folderId: string;
  folderTitle?: string;
  folderDescription?: string;
  folderIcon?: IconName;
  folderIconColor?: string;
  numberOfPermissions?: number;
};

export type ListArchiveFilter = {
  signingStatuses?: DocumentSigningStatus[];
  parentId?: string;
  rootId?: string;
  includeRootFolders?: boolean;
  status?: ArchiveDocumentStatus;
  types?: ArchiveDocumentType[];
  limit?: number;
  beforeEntry?: string;
  orderBy?: { field: "UPDATED" | "NAME"; descending?: boolean }[];
  starredOnly?: boolean;
};

export type ListArchiveResponse = {
  folderId?: string;
  entries: ArchiveEntry[];
  path?: ArchivePathNode[];
  fileUploadConfig: FileUploadConfig;
  limit?: number;
  beforeEntry?: string;
  numberOfPermissions: number;
  navigationTree?: ArchiveEntry;
};

export type RestoreRequest = {
  id: string;
};

export type RestoreResponse = {
  entry: ArchiveEntry;
  path: ArchivePathNode[];
};

type MoveRequest = { entryId: string; destinationId: string };

type RenameRequest = {
  id: string;
  name: string;
  description?: string;
  iconName?: string;
  iconColor?: string;
};

export type DestinationFolder = {
  id: string;
  type: ArchiveDocumentType;
  name: string;
  rootFolder?: ArchiveRootFolderType;
  rootFolderTitle?: string;
  projectId?: string;
  meetingType?: MeetingType;
  meetingDate?: string;
  rootFolderId?: string;
};

export type DestinationFoldersResponse = {
  entries: DestinationFolder[];
};

type CreateFolderRequest = {
  name: string;
  parentId?: string;
  organisationNumber: string;
  description?: string;
  iconName?: string;
  iconColor?: string;
};

export const archiveEntryUrl = (
  { organisationNumber }: { organisationNumber: string },
  entry: ArchiveEntry,
) => {
  if (entry.linkTargetType === ArchiveDocumentType.ATTACHMENT && entry.projectId) {
    return `/project/${entry.projectId}/attachment/${entry.linkTargetId}`;
  }
  return `/archive/${organisationNumber}/download/${entry.id}`;
};

export type ArchiveStatus = "ready" | "loading" | "uploading" | "drop-uploading";

export type ArchiveDialogState =
  | { type: "none" }
  | { type: "requires-subscription" }
  | CreateFolderState
  | RenameState
  | ConfirmDeleteArchiveForeverState
  | ConfirmEmptyTrashState
  | PreviewState
  | MoveState
  | SendForSigningState
  | SigningStatusState
  | ConfirmDeleteForeverState
  | ConfirmCancelSignatureRequestState
  | ConfirmRevokePermissionState;

export type CreateFolderState = {
  type: "create-folder";
  parentId: string | undefined;
  name: StringFieldModel;
  showDescription: BooleanFieldModel;
  description: LocalizedTextEditorModel;
  submitting?: boolean;
};

export type PreviewState = {
  type: "preview";
  entry: ArchiveEntry;
  file: { url: string };
  rendition?: PreviewRendition;
};

export type RenameState = {
  type: "rename";
  entryId: string;
  name: StringFieldModel;
  submitting?: boolean;
};

export type MoveState = {
  type: "move";
  entry: ArchiveEntry;
  destinationFolders: DestinationFolder[];
  selectedDestinationFolder?: DestinationFolder;
  confirmChange: boolean;
  isSubmitting: boolean;
};

export type ValidMoveState = {
  type: "move";
  entry: ArchiveEntry;
  destinationFolders: DestinationFolder[];
  selectedDestinationFolder: DestinationFolder;
  confirmChange: true;
  isSubmitting: false;
};

export type SendForSigningState = {
  type: "send-for-signing";
  entry: ArchiveEntry;
  form: SignersForm;
  title: TMessage;
  isSending?: boolean;
  conversionModel?: ConversionModel;
  disposeReaction?: () => void;
};

export type SigningStatusState = {
  type: "signing-status";
  dialogModel: ViewDocumentSigningStatusDialogModel;
};

export type ConfirmDeleteForeverState = {
  type: "confirm-delete-forever";
  entry: ArchiveEntry;
  loading?: boolean;
};

export type ConfirmCancelSignatureRequestState = {
  type: "confirm-delete-signature-request";
  entry: ArchiveEntry;
  onConfirmCancel?: DCEvent[];
  loading?: boolean;
};

export type ConfirmDeleteArchiveForeverState = {
  type: "confirm-delete-archive-forever";
  folderId: string;
  name: string;
  confirmChange: boolean;
  loading: boolean;
};

export type ConfirmEmptyTrashState = {
  type: "confirm-empty-trash";
  loading: boolean;
};

export type ConfirmRevokePermissionState = {
  type: "confirm-revoke-permission";
  folderId: string;
  permissionId: string;
  name: TMessage;
  loading: boolean;
};

export type ArchivePermissionSourceType = "ADMIN" | "CUSTOM_ROLE" | "BOARD_MEMBER" | "SHAREHOLDER";

const archivePermissionSourceTypeSortIndex: Record<ArchivePermissionSourceType | "", number> = {
  "": 0,
  ADMIN: 1,
  BOARD_MEMBER: 2,
  SHAREHOLDER: 3,
  CUSTOM_ROLE: 4,
};

export type ArchivePermissionSource = {
  type: ArchivePermissionSourceType;
  roleName?: string;
};

export type ArchiveFolderPermissionAction = "ADD";

export type ArchivePermissionEntryAction = "CHANGE" | "REMOVE";

export type ArchivePermission = {
  grantee: string;
  name: string;
  email?: string;
  privilege: PermissionPrivilege;
  source: ArchivePermissionSource;
  lastLogin?: string;
  permissionId?: string;
  actions: ArchivePermissionEntryAction[];
};

export type ArchivePermissionsResponse = {
  folderId: string;
  permissions: ArchivePermission[];
  groups: ArchivePermission[];
  actions: ArchiveFolderPermissionAction[];
};

export type ArchiveZipSummary = {
  files: number;
  bytes: number;
  missing: number;
  isAllowed: boolean;
  maxFiles: number;
  maxBytes: number;
};

type ArchiveApiClient = {
  getRootFolders: (
    orgNo: string,
    previewRoleId: string | undefined,
    params: HttpEventCallbacks<ArchiveRootFolder[]>,
  ) => HttpEvent<ArchiveRootFolder[]>;

  getPermissions: (
    orgNo: string,
    folderId: string,
    params: HttpEventCallbacks<ArchivePermissionsResponse>,
  ) => HttpEvent<ArchivePermissionsResponse>;

  revokePermission: (
    orgNo: string,
    folderId: string,
    permissionId: string,
    params: HttpEventCallbacks<ArchivePermissionsResponse>,
  ) => HttpEvent<ArchivePermissionsResponse>;

  listArchive: (
    orgNo: string,
    previewRoleId: string | undefined,
    data: ListArchiveFilter,
    params: HttpEventCallbacks<ListArchiveResponse>,
  ) => HttpEvent<ListArchiveResponse>;

  getDestinationFolders: (
    orgNo: string,
    entryId: string,
    params: HttpEventCallbacks<DestinationFoldersResponse>,
  ) => HttpEvent<DestinationFoldersResponse>;

  createFolder: (data: CreateFolderRequest, params: HttpEventCallbacks<void>) => HttpEvent<void>;

  emptyTrash: (orgNo: string, params: HttpEventCallbacks<void>) => HttpEvent<void>;

  restore: (
    data: RestoreRequest,
    params: HttpEventCallbacks<RestoreResponse>,
  ) => HttpEvent<RestoreResponse>;

  move: (orgNo: string, data: MoveRequest, params: HttpEventCallbacks<void>) => HttpEvent<void>;

  upload: (
    orgNo: string,
    folderId: string,
    files: File[],
    params: HttpEventCallbacks<void>,
  ) => HttpEvent<void>;

  rename: (
    data: RenameRequest,
    params: HttpEventCallbacks<ArchiveEntry>,
  ) => HttpEvent<ArchiveEntry>;

  star: (
    orgNo: string,
    data: { entryId: string; starred: boolean },
    params: HttpEventCallbacks<void>,
  ) => HttpEvent<void>;

  ensurePreview: (
    orgNo: string,
    entryId: string,
    params: HttpEventCallbacks<PreviewRendition>,
  ) => HttpEvent<PreviewRendition>;

  zipSummary: (
    orgNo: string,
    folderId: string,
    params: HttpEventCallbacks<ArchiveZipSummary>,
  ) => HttpEvent<ArchiveZipSummary>;

  delete: ({
    orgNo,
    entryId,
    permanent,
  }: {
    orgNo: string;
    entryId: string;
    permanent?: boolean;
  } & HttpEventCallbacks<void>) => HttpEvent<void>;
};

const archiveApiClient: ArchiveApiClient = {
  getRootFolders: (orgNo, previewRoleId, params) =>
    httpGet({
      url: `/archive/${orgNo}/root-folders${toQueryString({ previewRoleId })}`,
      ...params,
    }),

  getPermissions: (orgNo, folderId, params) =>
    httpGet({ url: `/archive/${orgNo}/permissions/${folderId}`, ...params }),

  revokePermission: (orgNo, folderId, permissionId, params) =>
    httpDelete({ url: `/archive/${orgNo}/permissions/${folderId}/${permissionId}`, ...params }),

  listArchive: (orgNo, previewRoleId, data, params) =>
    httpPost({ url: `/archive/${orgNo}/list${toQueryString({ previewRoleId })}`, data, ...params }),

  getDestinationFolders: (orgNo, entryId, params) =>
    httpGet({ url: `/archive/${orgNo}/destination-folders/${entryId}`, ...params }),

  createFolder: (data, params) => httpPost({ url: "/archive/create-folder", data, ...params }),

  emptyTrash: (orgNo, params) => httpPost({ url: `/archive/${orgNo}/empty-trash`, ...params }),

  restore: (data, params) => httpPost({ url: "/archive/restore", data, ...params }),

  move: (orgNo, data, params) => httpPost({ url: `/archive/${orgNo}/move`, data, ...params }),

  upload: (orgNo, folderId, files, params) => {
    const form = new FormData();
    files.forEach((f) => {
      form.append("files", f);
    });
    return httpPost({
      url: `/archive/${orgNo}/upload/${folderId}`,
      data: form,
      ...params,
    });
  },

  rename: (data, params) => httpPost({ url: "/archive/rename", data, ...params }),

  star: (orgNo, data, params) => httpPost({ url: `/archive/${orgNo}/star`, data, ...params }),

  ensurePreview: (orgNo, entryId, params) =>
    httpPost({ url: `/archive/${orgNo}/preview/${entryId}`, ...params }),

  zipSummary: (orgNo, folderId, params) =>
    httpGet({ url: `/archive/${orgNo}/download-zip/${folderId}/summary`, ...params }),

  delete: ({ orgNo, entryId, permanent, ...params }) =>
    httpDelete({
      url: `/archive/${orgNo}/${entryId}${toQueryString({ permanent })}`,
      ...params,
    }),
};

export type ArchiveSettings = {
  name?: {
    model: StringFieldModel;
    isSubmitting?: boolean;
  };
  description?: {
    model: StringFieldModel;
    isSubmitting?: boolean;
  };
};

export type ArchiveState = {
  organisationNumber: string;
  hasRequiredSubscription: boolean;
  userRole: CustomRoleForUser;
  rootFolders?: ArchiveRootFolder[];
  initError?: boolean;
  expandedEntries: Set<string>;
  selectedEntryId?: string;
  previewOnLoadEntryId?: string;
  rootFolder?: ArchiveRootFolderType;
  view: ArchiveRootFolderView;
  queryString: string;
  roleId?: string;
  data?: ListArchiveResponse;
  permissions?: ArchivePermissionsResponse;
  permissionsFilter?: {
    freetext?: string;
    source?: ArchivePermissionSource;
  };
  selectedPermission?: ArchivePermission;
  status: ArchiveStatus;
  dialog: ArchiveDialogState;
  currentActionByEntry: Record<ArchiveEntry["id"], ArchiveEntryAction | undefined>;
  textFilter: StringFieldModel;
  fileTypeFilter: RadioFieldModel<ArchiveFileTypeFilter>;
  signaturesFilter: RadioFieldModel<ArchiveSignaturesFilter>;
  loadToken: number;
  settings?: ArchiveSettings;
};

export const initialArchiveState = ({
  organisationNumber,
  hasRequiredSubscription,
  userRole,
}: InitArchivePageParams): ArchiveState => ({
  organisationNumber,
  hasRequiredSubscription,
  userRole,
  expandedEntries: new Set(),
  view: "files",
  queryString: "",
  status: "ready",
  dialog: { type: "none" },
  currentActionByEntry: {},
  textFilter: new StringFieldModel(),
  fileTypeFilter: createFileTypeFilterModel(),
  signaturesFilter: createSignaturesFilterModel(),
  loadToken: 0,
});

export const updateArchive = (data: Partial<ArchiveState>): UpdateStoreEvent => ({
  type: "update-store",
  data: { archive: (archive) => archive && { ...archive, ...data } },
});

const setInArchiveState = <K extends keyof ArchiveState>(
  key: K,
  valueOrCallback: ArchiveState[K] | ((current: ArchiveState) => ArchiveState[K]),
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    archive: (archive) =>
      archive && {
        ...archive,
        [key]: isFunction(valueOrCallback) ? valueOrCallback(archive) : valueOrCallback,
      },
  },
});

export const setArchiveDialog = (dialog: ArchiveDialogState): UpdateStoreEvent =>
  setInArchiveState("dialog", dialog);

const updateArchiveDialog = <T extends ArchiveDialogState["type"]>(
  type: T,
  update: (dialog: Extract<ArchiveDialogState, { type: T }>) => ArchiveDialogState,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    archive: (archive) =>
      archive && archive.dialog.type === type
        ? {
            ...archive,
            dialog: update(archive.dialog as Extract<ArchiveDialogState, { type: T }>),
          }
        : archive,
  },
});

const setCurrentEntryAction = (entryId: string, action: ArchiveEntryAction): UpdateStoreEvent =>
  setInArchiveState("currentActionByEntry", (archive) => ({
    ...archive.currentActionByEntry,
    [entryId]: action,
  }));

const clearCurrentEntryAction = (entryId: string): UpdateStoreEvent =>
  setInArchiveState("currentActionByEntry", (archive) =>
    omit(archive.currentActionByEntry, entryId),
  );

const setEntrySigningStatus = (
  entryId: string,
  signingStatus: DocumentSigningStatus,
): UpdateStoreEvent =>
  setInArchiveState(
    "data",
    (archive) =>
      archive.data && {
        ...archive.data,
        entries: archive.data.entries.map((e) => (e.id === entryId ? { ...e, signingStatus } : e)),
      },
  );

const setEntryStarred = (entryId: string, isStarred: boolean): UpdateStoreEvent =>
  setInArchiveState(
    "data",
    (archive) =>
      archive.data && {
        ...archive.data,
        entries: archive.data.entries.map((e) => (e.id === entryId ? { ...e, isStarred } : e)),
      },
  );

export const canStarEntry = (entry: ArchiveEntry): boolean =>
  entry.type === ArchiveDocumentType.FILE || entry.isFolder === true;

export const toggleEntryStarred = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => {
  const isStarred = !entry.isStarred;
  return [
    actionName("archive::admin-archive::toggleStarred", toJS(entry)),
    setEntryStarred(entry.id, isStarred),
    archiveApiClient.star(
      state.organisationNumber,
      { entryId: entry.id, starred: isStarred },
      {
        onSuccess: (_, root) =>
          root.archive && root.archive.rootFolder === ArchiveRootFolderType.STARRED
            ? loadArchiveEvents(root.archive)
            : [],
        onFailure: () => [setEntryStarred(entry.id, !isStarred), showGeneralError()],
      },
    ),
  ];
};

export const rootFolderFromPath = (state: ArchiveState): ArchivePathNode | undefined =>
  state.data?.path?.[0];

export const rootFolderIdFromPath = (state: ArchiveState): string | undefined =>
  rootFolderFromPath(state)?.id;

export const getFolderId = (state: ArchiveState): string | undefined =>
  singleValue(fromQueryString(state.queryString).folderId) ?? rootFolderIdFromPath(state);

export const selectedRootFolder = (state: ArchiveState): ArchiveRootFolder | undefined =>
  state.rootFolders?.find((f) => f.folderId === getFolderId(state)) ??
  state.rootFolders?.find((f) => f.folderId === rootFolderIdFromPath(state));

export const getRootFolderId = (state: ArchiveState): string =>
  selectedRootFolder(state)?.folderId ??
  findRootFolder(
    state.rootFolders ?? [],
    state.rootFolder ?? ArchiveRootFolderType.ADMIN_ARCHIVE,
    rootFolderIdFromPath(state),
  )?.folderId ??
  "";

export const getArchiveEntries = (state: ArchiveState): ArchiveEntry[] => state.data?.entries ?? [];

export const getFileUploadConfig = (state: ArchiveState): FileUploadConfig =>
  state.data?.fileUploadConfig ?? emptyFileUploadConfig();

export const isInBoardMeetingsFolder = (state: ArchiveState): boolean =>
  last(state.data?.path)?.type === ArchiveDocumentType.BOARD_MEETINGS_FOLDER;

export const canEditArchive = (state: ArchiveState): boolean =>
  state.rootFolder !== undefined && allowArchiveEdit(state.rootFolder, state.userRole);

export const canUploadFiles = (state: ArchiveState): boolean =>
  state.rootFolder !== undefined &&
  canUpload({
    rootFolder: state.rootFolder,
    userRole: state.userRole,
    data: state.data,
    view: state.view,
  });

export const isEntryExpanded = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  state.expandedEntries.has(entry.id);

export const isEntrySelected = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  getFolderId(state) === entry.id;

export const isEntryRowSelected = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  state.selectedEntryId === entry.id || isPreviewing(state, entry);

const isPreviewing = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  state.dialog.type === "preview" && state.dialog.entry.id === entry.id;

export const isEntryLoading = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  state.status === "loading" && isEntrySelected(state, entry);

export const isPerformingEntryAction = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  state.currentActionByEntry[entry.id] !== undefined;

export const getPrimaryEntryAction = (
  state: ArchiveState,
  entry: ArchiveEntry,
): ArchiveEntryAction | undefined =>
  state.rootFolder === undefined
    ? undefined
    : primaryEntryActionFor(
        state.rootFolder,
        state.view,
        entry,
        getArchiveEntries(state),
        state.userRole,
      );

export const getEntryActions = (
  state: ArchiveState,
  entry: ArchiveEntry,
): ArchiveEntryAction[] | undefined =>
  state.rootFolder === undefined
    ? undefined
    : entryActionsFor(
        state.rootFolder,
        state.view,
        entry,
        getArchiveEntries(state),
        state.userRole,
      );

export const getSendForSigningButtonProps = (
  context: ArchiveContext,
  state: ArchiveState,
  entry: ArchiveEntry,
): LinkButtonProps | undefined =>
  when(
    getEntryActions(state, entry)?.find((x) => x === "send-for-signing"),
    (action) => ({
      icon: getActionIcon(action),
      label: getActionLabel(action),
      loading: isPerformingEntryAction(state, entry),
      onClick: () => performEntryAction(context, state, action, entry),
    }),
  );

export const canRemoveSigner = (dialog: SendForSigningState): boolean =>
  dialog.form.items.length > 1 || !dialog.form.items[0].isEmpty;

export const toggleEntryExpanded = (entry: ArchiveEntry): UpdateStoreEvent =>
  setInArchiveState("expandedEntries", (archive) => {
    const expanded = new Set(archive.expandedEntries);
    if (expanded.has(entry.id)) {
      expanded.delete(entry.id);
    } else {
      expanded.add(entry.id);
    }
    return expanded;
  });

export const ARCHIVE_ENTRIES_ID = "archive-entries";

const ENTRY_ROW_SELECTOR = `#${ARCHIVE_ENTRIES_ID} tr[data-row-id]`;

const getEntryRows = (): HTMLElement[] =>
  Array.from(document.querySelectorAll<HTMLElement>(ENTRY_ROW_SELECTOR));

const getEntryRowFor = (target: EventTarget | null): HTMLElement | undefined =>
  target instanceof Element
    ? (target.closest<HTMLElement>(ENTRY_ROW_SELECTOR) ?? undefined)
    : undefined;

export const scrollEntryIntoView = (entryId: string | undefined): void =>
  getEntryRows()
    .find((row) => row.dataset.rowId === entryId)
    ?.scrollIntoView({ block: "nearest" });

export const handleEntryFocus = (
  state: ArchiveState,
  event: FocusEvent | React.FocusEvent,
): DCEvent[] => {
  const entryId = getEntryRowFor(event.target)?.dataset.rowId;
  const entry = getArchiveEntries(state).find((e) => e.id === entryId);
  return entry && entry.id !== state.selectedEntryId ? selectEntry(state, entry) : [];
};

const selectEntry = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => {
  const nextState: ArchiveState = { ...state, selectedEntryId: entry.id };
  return [
    updateArchive(nextState),
    ...(state.dialog.type === "preview" ? openPreviewDialog(nextState, entry) : []),
  ];
};

const isKeyForEventTarget = (event: KeyboardEvent): boolean =>
  event.defaultPrevented ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  isTargetInput(event) ||
  (event.target instanceof Element &&
    event.target.closest("[role='dialog'], [role='alertdialog']") !== null);

export const handleKeyDown = (state: ArchiveState, event: KeyboardEvent): DCEvent[] => {
  switch (true) {
    case isKeyForEventTarget(event):
      return [];

    case isArrowUp(event):
    case isArrowDown(event): {
      event.preventDefault();
      const step = isArrowUp(event) ? -1 : 1;
      const rows = getEntryRows();
      const current =
        getEntryRowFor(event.target) ??
        rows.find((row) => row.dataset.rowId === state.selectedEntryId);
      const next = current ? rows[rows.indexOf(current) + step] : rows.at(step > 0 ? 0 : -1);
      return when(next, (row) => [thunk(() => row.focus())]) ?? [];
    }

    default:
      return [];
  }
};

const withExpandedPath = (
  expandedEntries: Set<string>,
  path: ArchivePathNode[] | undefined,
): Set<string> => new Set([...expandedEntries, ...(path ?? []).map((p) => p.id)]);

export type InitArchivePageParams = {
  organisationNumber: string;
  hasRequiredSubscription: boolean;
  userRole: CustomRoleForUser;
  previewRoleId?: string;
  rootFolder?: string;
  view?: string;
  queryString?: string;
};

export const initArchivePage = (params: InitArchivePageParams): DCEvent[] => [
  { type: "with-store", events: (root) => initArchivePageEvents(root.archive, params) },
];

const initArchivePageEvents = (
  archive: ArchiveState | undefined,
  params: InitArchivePageParams,
): DCEvent[] => {
  // General meetings was an archive root of its own until the archive extraction; it is a folder
  // in the shareholder archive now, so links still pointing at it keep their folderId and land
  // there.
  if (params.rootFolder === ArchiveRootFolderType.GENERAL_MEETINGS) {
    return [
      navigate(
        adminArchive({
          orgNo: params.organisationNumber,
          folderType: ArchiveRootFolderType.SHAREHOLDER_ARCHIVE,
          folderId: singleValue(fromQueryString(params.queryString).folderId),
          view: params.view,
        }),
        { replace: true },
      ),
    ];
  }
  const base: ArchiveState = {
    ...(archive && archive.organisationNumber === params.organisationNumber
      ? archive
      : initialArchiveState(params)),
    hasRequiredSubscription: params.hasRequiredSubscription,
    userRole: params.userRole,
    roleId: params.previewRoleId,
    dialog: { type: "none" },
  };
  if (!base.rootFolders || archive?.roleId !== params.previewRoleId) {
    return [updateStore({ archive: base }), loadRootFoldersEvent(base, initArchivePage(params))];
  }
  const { rootFolder, view, queryString } = params;
  if (
    !isRootFolderType(rootFolder) ||
    (rootFolder !== ArchiveRootFolderType.RECENT &&
      rootFolder !== ArchiveRootFolderType.STARRED &&
      !base.rootFolders.some((x) => x.folderType === rootFolder))
  ) {
    return [updateStore({ archive: { ...base, rootFolder: undefined } })];
  }
  const nextView = viewForRootFolder(view, rootFolder);
  const keepData = nextView === base.view && isSameRootFolder(base, rootFolder, queryString);
  const next: ArchiveState = {
    ...base,
    rootFolder,
    view: nextView,
    queryString: queryString ?? "",
    data: keepData ? base.data : undefined,
    permissions: keepData ? base.permissions : undefined,
    status: "loading",
    dialog: { type: "none" },
    currentActionByEntry: {},
    textFilter: new StringFieldModel(),
    fileTypeFilter: createFileTypeFilterModel(),
    signaturesFilter: createSignaturesFilterModel(),
  };
  return [updateStore({ archive: next }), ...loadArchiveEvents({ ...next, data: undefined })];
};

const isSameRootFolder = (
  base: ArchiveState,
  rootFolder: ArchiveRootFolderType,
  queryString: string | undefined,
): boolean => {
  if (rootFolder !== base.rootFolder) {
    return false;
  }
  const targetRootFolder = base.rootFolders?.find(
    (f) => f.folderId === fromQueryString(queryString).folderId,
  );
  return targetRootFolder === undefined || targetRootFolder.folderId === rootFolderIdFromPath(base);
};

const loadRootFoldersEvent = (state: ArchiveState, onSuccess: DCEvent[] = []): HttpEvent =>
  archiveApiClient.getRootFolders(state.organisationNumber, state.roleId, {
    onSuccess: (res) => [
      setInArchiveState("rootFolders", filterByPermission(res.data, state.userRole)),
      ...onSuccess,
    ],
    onFailure: () => [setInArchiveState("initError", true)],
  });

const refreshRootFolders = (): WithStoreEvent => ({
  type: "with-store",
  events: (root) => (root.archive ? [loadRootFoldersEvent(root.archive)] : []),
});

export const reloadArchive = (): WithStoreEvent => ({
  type: "with-store",
  events: (root) => (root.archive ? loadArchiveEvents(root.archive) : []),
});

export const loadArchiveEvents = (state: ArchiveState): DCEvent[] => {
  const token = state.loadToken + 1;
  return [
    setInArchiveState("loadToken", token),
    archiveApiClient.listArchive(
      state.organisationNumber,
      state.roleId,
      toListArchiveFilter({
        view: state.view,
        folderId: getFolderId(state),
        rootFolder: state.rootFolder,
        rootFolderId: getRootFolderId(state),
      }),
      {
        onSuccess: (res, root) =>
          root.archive?.loadToken === token
            ? [
                updateArchive({
                  data: res.data,
                  status: "ready",
                  expandedEntries: withExpandedPath(root.archive.expandedEntries, res.data.path),
                  previewOnLoadEntryId: undefined,
                }),
                ...previewOnLoadEvents(root.archive, res.data.entries),
                ...(root.archive.view === "permissions"
                  ? loadArchivePermissionsEvents(root.archive, res.data.path?.[0]?.id)
                  : []),
              ]
            : [],
        onFailure: (_, root) =>
          root.archive?.loadToken === token
            ? [
                showError({ key: "archive.fetchError" }),
                updateArchive({ status: "ready", previewOnLoadEntryId: undefined }),
              ]
            : [],
      },
    ),
  ];
};

const previewOnLoadEvents = (state: ArchiveState, entries: ArchiveEntry[]): DCEvent[] =>
  when(
    entries.find((e) => e.id === state.previewOnLoadEntryId),
    (entry) => [updateArchive({ selectedEntryId: entry.id }), ...openPreviewDialog(state, entry)],
  ) ?? [];

const loadArchivePermissionsEvents = (
  state: ArchiveState,
  rootFolderId: string | undefined,
): DCEvent[] =>
  rootFolderId === undefined
    ? []
    : [fetchArchivePermissions(state.organisationNumber, rootFolderId)];

const fetchArchivePermissions = (
  organisationNumber: string,
  rootFolderId: string,
): HttpEvent<ArchivePermissionsResponse> =>
  archiveApiClient.getPermissions(organisationNumber, rootFolderId, {
    onSuccess: (res) => [setInArchiveState("permissions", res.data)],
    onFailure: () => [showError({ key: "archive.fetchError" })],
  });

const permissionsWithSource = (
  state: ArchiveState,
  source: ArchivePermissionSource | undefined,
): ArchivePermission[] => {
  const xs = state.permissions?.permissions ?? [];
  if (source) return xs.filter((x) => isEqual(x.source, source));
  return Object.values(groupBy(xs, (x) => x.grantee)).map(
    (same) => maxBy(same, (x) => privilegeRank[x.privilege]) ?? same[0],
  );
};

export const getPermissions = (state: ArchiveState): ArchivePermission[] =>
  permissionsWithSource(state, state.permissionsFilter?.source);

export const hasPermissionSourceLabels = (permissions: ArchivePermission[]): boolean =>
  permissions.every((x) => permissionSourceLabel(x) !== undefined);

export const permissionSourceLabel = (permission: ArchivePermission): TMessage | undefined => {
  switch (permission.source.type) {
    case "ADMIN":
      return { key: "accessType.ADMIN" };
    case "BOARD_MEMBER":
      return { key: "accessType.BOARD_MEMBER" };
    case "SHAREHOLDER":
      return { key: "accessType.SHAREHOLDER" };
    case "CUSTOM_ROLE":
      return permission.source.roleName === undefined
        ? undefined
        : { translated: permission.source.roleName };
  }
};

export type ArchivePermissionSourceFilterProps = {
  source?: ArchivePermissionSource;
  icon: IconName;
  label: TMessage;
  count: number;
  description?: TMessage;
  isActive?: boolean;
  activate: DCEvent[];
};

const permissionSourceFilter = (
  state: ArchiveState,
  source: ArchivePermissionSource | undefined,
): ArchivePermissionSourceFilterProps => {
  const group = source && state.permissions?.groups.find((g) => isEqual(g.source, source));
  return {
    source: source,
    icon: !source ? "users-three" : "folder-user",
    label: !source
      ? { key: "archive.permissions.source-filter.all" }
      : source.roleName
        ? { translated: source.roleName }
        : { key: `archive.permissions.source-filter.${source.type}` },
    description: group && permissionPrivilegeLabel(group.privilege),
    count: permissionsWithSource(state, source).length,
    isActive: isEqual(state.permissionsFilter?.source, source),
    activate: [
      updateArchive({
        ...state,
        permissionsFilter: { ...state.permissionsFilter, source: source },
      }),
    ],
  };
};

export const prepPermissionSourceFilters = (
  state: ArchiveState,
): ArchivePermissionSourceFilterProps[] => {
  if (!state.permissions) return [];
  return sortBy(
    [
      permissionSourceFilter(state, undefined),
      ...uniqWith(
        [...state.permissions.groups, ...state.permissions.permissions].map((s) => s.source),
        isEqual,
      ).map((s) => permissionSourceFilter(state, s)),
    ],
    (x) => archivePermissionSourceTypeSortIndex[x.source?.type ?? ""],
    (x) => x.source?.roleName,
  );
};

export const updatePermissionFreetextFilter = (state: ArchiveState, s: string): DCEvent[] => [
  updateArchive({
    permissionsFilter: {
      ...state.permissionsFilter,
      freetext: s,
    },
  }),
];

export const selectPermission = (permission: ArchivePermission | undefined) => [
  updateArchive({ selectedPermission: permission }),
];

export const getSelectedPermission = (state: ArchiveState): ArchivePermission | undefined => {
  const selected = state.selectedPermission;
  const permissions = getPermissions(state);
  if (selected && permissions.includes(selected)) {
    return selected;
  }
};

export const prepPermissionDetailsOptions = (
  state: ArchiveState,
  p: ArchivePermission,
): DropdownMenuProps | undefined =>
  !p.actions.includes("REMOVE")
    ? undefined
    : {
        button: { type: "link", label: { key: "options" } },
        items: [
          {
            type: "item",
            icon: "user-minus",
            label: { key: "permissions.revoke-access" },
            color: "error",
            onSelect: openRevokePermissionDialog(state, p),
          },
        ],
        alignment: "end",
      };

export const openRevokePermissionDialog = (
  state: ArchiveState,
  p: ArchivePermission,
): DCEvent[] => {
  const folderId = state.permissions?.folderId;
  if (folderId === undefined || p.permissionId === undefined) return [];
  return [
    setArchiveDialog({
      type: "confirm-revoke-permission",
      folderId,
      permissionId: p.permissionId,
      name: granteeLabel(p),
      loading: false,
    }),
  ];
};

export const confirmRevokePermission = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "confirm-revoke-permission") return [];
  return [
    updateArchiveDialog("confirm-revoke-permission", (d) => ({ ...d, loading: true })),
    archiveApiClient.revokePermission(
      state.organisationNumber,
      dialog.folderId,
      dialog.permissionId,
      {
        onSuccess: (res) => [
          updateArchive({ permissions: res.data, selectedPermission: undefined }),
          closeArchiveDialog(),
          showSuccess({ key: "archive.permissions.revoke-success" }, { autoHide: true }),
        ],
        onFailure: () => [
          closeArchiveDialog(),
          showError({ key: "archive.permissions.revoke-error" }),
        ],
      },
    ),
  ];
};

export const canAddPermissions = (state: ArchiveState): boolean =>
  state.permissions?.actions.includes("ADD") ?? false;

export function toListArchiveFilter({
  rootFolder,
  rootFolderId,
  folderId,
  view,
}: {
  rootFolder?: ArchiveRootFolderType;
  rootFolderId?: string;
  folderId?: string;
  view?: ArchiveRootFolderView;
}): ListArchiveFilter {
  rootFolderId = isBlank(rootFolderId) ? undefined : rootFolderId;
  switch (true) {
    case view === "deleted":
      return {
        status: ArchiveDocumentStatus.DELETED,
        orderBy: [{ field: "UPDATED", descending: true }],
        rootId: rootFolderId,
      };
    case rootFolder === ArchiveRootFolderType.RECENT:
      return {
        orderBy: [{ field: "UPDATED", descending: true }],
        rootId: rootFolderId,
        types: [ArchiveDocumentType.FILE],
        limit: 100,
      };
    case rootFolder === ArchiveRootFolderType.STARRED:
      return {
        orderBy: [{ field: "UPDATED", descending: true }],
        rootId: rootFolderId,
        starredOnly: true,
        limit: 100,
      };
    default:
      return { parentId: folderId ?? rootFolderId };
  }
}

export const navigateArchive = (
  state: ArchiveState,
  {
    rootFolder,
    rootFolderId,
    folderId,
    replace,
    view,
  }: {
    rootFolder?: ArchiveRootFolderType;
    rootFolderId?: string;
    folderId?: string;
    replace?: boolean;
    view?: ArchiveRootFolderView;
  },
): NavigateEvent =>
  navigate(
    adminArchive({
      orgNo: state.organisationNumber,
      folderType: rootFolderId
        ? state.rootFolders?.find((f) => f.folderId === rootFolderId)?.folderType
        : rootFolder,
      folderId,
      view,
    }),
    { replace },
  );

const navigateToContainingFolder = (state: ArchiveState, entry: ArchiveEntry): NavigateEvent =>
  navigateArchive(state, { rootFolderId: entry.rootId, folderId: entry.parentId });

export const navigateToArchiveFolder = (
  state: ArchiveState,
  rootFolder: ArchiveRootFolder | undefined,
  view?: ArchiveRootFolderView,
): NavigateEvent =>
  navigateArchive(state, {
    rootFolder: rootFolder?.folderType,
    folderId: rootFolder?.folderId ? rootFolder.folderId : undefined,
    view,
  });

export const handleBreadcrumbClick = (state: ArchiveState, id: string): NavigateEvent =>
  id === state.rootFolder
    ? navigateArchive(state, { rootFolder: state.rootFolder })
    : navigateArchive(state, { rootFolder: state.rootFolder, folderId: id });

export const navigateToNewProject = (state: ArchiveState): NavigateEvent =>
  navigate(adminProjects(state.organisationNumber));

export const closeArchiveDialog = (): DCEvent => {
  return {
    type: "with-store",
    events: ({ archive: state }) => {
      if (!state) {
        return [];
      }
      const dialog = state.dialog;
      switch (dialog.type) {
        case "rename":
        case "create-folder":
          return dialog.submitting ? [] : [setArchiveDialog({ type: "none" })];
        case "preview":
          return [
            { type: "clear-timeout", timeoutId: PREVIEW_POLL_TIMEOUT_ID },
            setArchiveDialog({ type: "none" }),
          ];
        case "send-for-signing":
          return [
            thunk(() => {
              dialog.disposeReaction?.();
            }),
            setArchiveDialog({ type: "none" }),
          ];
        default:
          return [setArchiveDialog({ type: "none" })];
      }
    },
  };
};

const requireSubscriptionEvents = (): DCEvent[] => [
  updateArchive({
    dialog: { type: "requires-subscription" },
    status: "ready",
    currentActionByEntry: {},
  }),
];

export const openCreateFolderDialog = (
  context: Pick<ArchiveContext, "http">,
  state: ArchiveState,
): DCEvent[] => [
  setArchiveDialog(
    createCreateFolderState({
      existingEntries: getExistingNames(getArchiveEntries(state)),
      parentId: getFolderId(state),
      aiClient: new AIClient(context.http),
    }),
  ),
];

export const createFolderAction = (
  context: ArchiveContext,
  archive: ArchiveState,
): { variant: ButtonProps["variant"]; icon: IconName; label: TMessage; onClick: DCEvent[] } => ({
  variant: "secondary",
  icon: "plus",
  label: { key: "archive.create-new-folder" },
  onClick: openCreateFolderDialog(context, archive),
});

export const uploadAction = (archive: ArchiveState): SelectFilesButtonProps => ({
  icon: "upload",
  label: { key: "upload" },
  fileUploadConfig: getFileUploadConfig(archive),
  onChange: (files, fileUploadConfig) => handleFilesChange(archive, files, fileUploadConfig),
  variant: "secondary",
  isLoading: archive.status === "uploading",
});

export const downloadFolderAction = (
  archive: ArchiveState,
  t: TFunction,
): { icon: IconName; tooltip: TMessage; loading: boolean; onClick: DCEvent[] } | undefined =>
  when(
    archive.view !== "deleted" &&
      !isListRootFolder(archive.rootFolder) &&
      archive.data?.path?.at(-1),
    (folder) => ({
      icon: "download",
      tooltip: { key: "archive.download-folder" },
      loading: archive.currentActionByEntry[folder.id] !== undefined,
      onClick: downloadFolder(archive, folder.id, t(getEntryLabel(folder))),
    }),
  );

export const folderActionsDropdown = (
  context: ArchiveContext,
  archive: ArchiveState,
): Array<DropdownMenuItemType> | undefined =>
  canUploadFiles(archive)
    ? [
        withValue(createFolderAction(context, archive), (action) => ({
          type: "item",
          icon: action.icon,
          label: action.label,
          onSelect: action.onClick,
        })),
        withValue(uploadAction(archive), (action) => ({
          type: "file-upload",
          label: action.label,
          fileUploadConfig: action.fileUploadConfig,
          onSelect: action.onChange,
        })),
      ]
    : undefined;

export const openCreateCustomArchiveDialog = (
  context: Pick<ArchiveContext, "aiClient">,

  state: ArchiveState,
): DCEvent[] => [
  setArchiveDialog(
    createCreateFolderState({
      existingEntries: getExistingNames(rootFolderNames(state.rootFolders ?? [])),
      parentId: undefined,
      aiClient: context.aiClient,
    }),
  ),
];

export const submitCreateFolder = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "create-folder" || dialog.submitting || !validateCreateFolderState(dialog)) {
    return [];
  }
  const isRootFolder = dialog.parentId === undefined;
  return [
    updateArchiveDialog("create-folder", (d) => ({ ...d, submitting: true })),
    archiveApiClient.createFolder(
      {
        organisationNumber: state.organisationNumber,
        name: dialog.name.inputValue,
        parentId: dialog.parentId,
        description: dialog.description.textModels[0].model.isEmpty
          ? undefined
          : dialog.description.textModels[0].model.inputValue,
      },
      {
        onSuccess: () => [
          updateArchiveDialog("create-folder", (d) => ({ ...d, submitting: false })),
          closeArchiveDialog(),
          isRootFolder ? refreshRootFolders() : reloadArchive(),
        ],
        onFailure: () => [closeArchiveDialog(), showError({ key: "archive.create-folder-error" })],
      },
    ),
  ];
};

export const openRenameDialog = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => [
  setArchiveDialog(
    createRenameState({
      existingEntries: getExistingNames(getArchiveEntries(state)),
      entryId: entry.id,
      name: entry.name,
    }),
  ),
];

export const submitRename = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "rename" || dialog.submitting) {
    return [];
  }
  if (!dialog.name.isChanged) {
    return [closeArchiveDialog()];
  }
  if (!validateRenameState(dialog)) {
    return [];
  }
  return [
    updateArchiveDialog("rename", (d) => ({ ...d, submitting: true })),
    archiveApiClient.rename(
      {
        id: dialog.entryId,
        name: dialog.name.inputValue,
      },
      {
        onSuccess: (res: HttpResponse<ArchiveEntry>) => [
          updateArchiveDialog("rename", (d) => ({ ...d, submitting: false })),
          setInArchiveState(
            "data",
            (archive) =>
              archive.data && {
                ...archive.data,
                entries: replaceEntry(archive.data.entries, res.data),
              },
          ),
          refreshRootFolders(),
          reloadArchive(),
          closeArchiveDialog(),
        ],
        onFailure: () => [
          updateArchiveDialog("rename", (d) => ({ ...d, submitting: false })),
          showError({ key: "general-error" }),
        ],
      },
    ),
  ];
};

export const selectedCustomArchive = (state: ArchiveState): ArchiveRootFolder | undefined => {
  const rootFolder = selectedRootFolder(state);
  return rootFolder?.folderType === ArchiveRootFolderType.CUSTOM_ARCHIVE ? rootFolder : undefined;
};

export const editArchiveName = (state: ArchiveState): DCEvent[] => {
  const rootFolder = selectedCustomArchive(state);
  if (rootFolder === undefined) {
    return [];
  }
  return [
    setInArchiveState("settings", {
      name: {
        model: createArchiveNameModel(
          getExistingNames(rootFolderNames(state.rootFolders ?? []), rootFolder.folderId),
          rootFolder.folderTitle ?? "",
        ),
      },
    }),
  ];
};

export const editArchiveDescription = (state: ArchiveState): DCEvent[] => {
  const rootFolder = selectedCustomArchive(state);
  if (rootFolder === undefined) {
    return [];
  }
  return [
    setInArchiveState("settings", {
      description: {
        model: createArchiveDescriptionModel(rootFolder.folderDescription),
      },
    }),
  ];
};

export const cancelArchiveSettingsEdit = (state: ArchiveState): DCEvent[] =>
  state.settings?.name?.isSubmitting || state.settings?.description?.isSubmitting
    ? []
    : [setInArchiveState("settings", undefined)];

export const submitArchiveName = (state: ArchiveState): DCEvent[] => {
  const edit = state.settings?.name;
  const rootFolder = selectedCustomArchive(state);
  if (edit === undefined || edit.isSubmitting || rootFolder === undefined) {
    return [];
  }
  if (!edit.model.isChanged) {
    return [setInArchiveState("settings", undefined)];
  }
  if (!edit.model.validate()) {
    return [];
  }
  return submitArchiveSettings(rootFolder, "name", {
    name: edit.model.validatedValue,
    description: rootFolder.folderDescription,
  });
};

export const submitArchiveDescription = (state: ArchiveState): DCEvent[] => {
  const edit = state.settings?.description;
  const rootFolder = selectedCustomArchive(state);
  if (edit === undefined || edit.isSubmitting || rootFolder === undefined) {
    return [];
  }
  return submitArchiveSettings(rootFolder, "description", {
    name: rootFolder.folderTitle ?? "",
    description: edit.model.validatedValue,
  });
};

const submitArchiveSettings = (
  rootFolder: ArchiveRootFolder,
  field: keyof ArchiveSettings,
  values: { name: string; description?: string },
): DCEvent[] => [
  setArchiveSettingsSubmitting(field, true),
  archiveApiClient.rename(
    { id: rootFolder.folderId, ...values },
    {
      onSuccess: () => [
        refreshRootFolders(),
        reloadArchive(),
        setInArchiveState("settings", undefined),
      ],
      onFailure: () => [
        setArchiveSettingsSubmitting(field, false),
        showError({ key: "general-error" }),
      ],
    },
  ),
];

const setArchiveSettingsSubmitting = (
  field: keyof ArchiveSettings,
  isSubmitting: boolean,
): UpdateStoreEvent =>
  setInArchiveState("settings", (archive) => {
    switch (field) {
      case "name":
        return archive.settings?.name && { name: { ...archive.settings.name, isSubmitting } };
      case "description":
        return (
          archive.settings?.description && {
            description: { ...archive.settings.description, isSubmitting },
          }
        );
    }
  });

export const deleteArchive = (state: ArchiveState): DCEvent[] =>
  when(selectedCustomArchive(state), (folder) => [
    setArchiveDialog(createDeleteArchiveState(folder.folderId, folder.folderTitle ?? "")),
  ]) ?? [];

export const confirmDeleteArchiveForever = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "confirm-delete-archive-forever") {
    return [];
  }
  return [
    updateArchiveDialog("confirm-delete-archive-forever", (d) => ({ ...d, loading: true })),
    archiveApiClient.delete({
      orgNo: state.organisationNumber,
      entryId: dialog.folderId,
      permanent: true,
      onSuccess: () => [refreshRootFolders(), closeArchiveDialog(), navigateArchive(state, {})],
      onFailure: () => [
        updateArchiveDialog("confirm-delete-archive-forever", (d) => ({ ...d, loading: false })),
        showError({ key: "archive.delete-error" }),
      ],
    }),
  ];
};

export const openEmptyTrashDialog = (): DCEvent[] => [setArchiveDialog(createEmptyTrashState())];

export const confirmEmptyTrash = (state: ArchiveState): DCEvent[] => {
  if (state.dialog.type !== "confirm-empty-trash") {
    return [];
  }
  return [
    updateArchiveDialog("confirm-empty-trash", (d) => ({ ...d, loading: true })),
    archiveApiClient.emptyTrash(state.organisationNumber, {
      onSuccess: () => [
        closeArchiveDialog(),
        setInArchiveState("status", "loading"),
        showSuccess({ key: "archive.empty-trash-success" }),
        reloadArchive(),
      ],
      onFailure: () => [
        updateArchiveDialog("confirm-empty-trash", (d) => ({ ...d, loading: false })),
        showError({ key: "archive.empty-trash-error" }),
      ],
    }),
  ];
};

export const deleteEntryEvents = (
  state: ArchiveState,
  entryId: string,
  permanent?: boolean,
): DCEvent[] => [
  archiveApiClient.delete({
    orgNo: state.organisationNumber,
    entryId,
    permanent,
    onSuccess: (_, root) => [
      ...entryDeleteSettled(root, entryId),
      closeArchiveDialog(),
      showGeneralSuccess(),
      reloadArchive(),
    ],
    onFailure: (_, root) => [
      ...entryDeleteSettled(root, entryId),
      showError({ key: "archive.delete-error" }),
    ],
  }),
];

const entryDeleteSettled = (root: RootStoreData, entryId: string): DCEvent[] => [
  clearCurrentEntryAction(entryId),
  ...(root.archive?.dialog.type === "confirm-delete-forever" ||
  root.archive?.dialog.type === "confirm-delete-signature-request"
    ? [closeArchiveDialog()]
    : []),
];

export const openConfirmDeleteForeverDialog = (entry: ArchiveEntry): DCEvent[] => [
  setArchiveDialog({ type: "confirm-delete-forever", entry }),
];

export const confirmDeleteForever = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "confirm-delete-forever") {
    return [];
  }
  return [
    updateArchiveDialog("confirm-delete-forever", (d) => ({ ...d, loading: true })),
    ...deleteEntryEvents(state, dialog.entry.id, true),
  ];
};

const isListRootFolder = (rootFolder: ArchiveRootFolderType | undefined): boolean =>
  rootFolder === ArchiveRootFolderType.RECENT ||
  rootFolder === ArchiveRootFolderType.STARRED ||
  rootFolder === ArchiveRootFolderType.SEND_FOR_SIGNING;

export const archiveZipUrl = (organisationNumber: string, folderId: string, name: string) =>
  `/api/archive/${organisationNumber}/download-zip/${folderId}${toQueryString({ name })}`;

export const downloadFolder = (state: ArchiveState, folderId: string, name: string): DCEvent[] => {
  if (!state.hasRequiredSubscription) {
    return requireSubscriptionEvents();
  }
  return [
    setCurrentEntryAction(folderId, "download"),
    archiveApiClient.zipSummary(state.organisationNumber, folderId, {
      onSuccess: ({ data }: HttpResponse<ArchiveZipSummary>) => [
        clearCurrentEntryAction(folderId),
        data.isAllowed
          ? loadUrl(archiveZipUrl(state.organisationNumber, folderId, name), false)
          : showError({
              key: "archive.download-folder-too-large",
              params: {
                maxFiles: data.maxFiles,
                maxMegabytes: Math.round(data.maxBytes / 1024 / 1024),
              },
            }),
      ],
      onFailure: () => [
        clearCurrentEntryAction(folderId),
        showError({ key: "archive.download-error" }),
      ],
    }),
  ];
};

export const downloadEntry = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => {
  if (!state.hasRequiredSubscription) {
    return requireSubscriptionEvents();
  }
  return [
    {
      type: "download-file",
      url: archiveEntryUrl(state, entry),
      onSuccess: [clearCurrentEntryAction(entry.id)],
      onFailure: [clearCurrentEntryAction(entry.id), showError({ key: "archive.download-error" })],
    },
  ];
};

export const restoreEntry = (state: ArchiveState, entryId: string): DCEvent[] => [
  archiveApiClient.restore(
    { id: entryId },
    {
      onSuccess: (res: HttpResponse<RestoreResponse>) => {
        const { rootFolder, folderId } = restoreResponseToNavigationTarget(res.data);
        return [clearCurrentEntryAction(entryId), navigateArchive(state, { rootFolder, folderId })];
      },
      onFailure: () => [
        clearCurrentEntryAction(entryId),
        showError({ key: "archive.restore-error" }),
      ],
    },
  ),
];

export const requestMove = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => [
  setCurrentEntryAction(entry.id, "move"),
  archiveApiClient.getDestinationFolders(state.organisationNumber, entry.id, {
    onSuccess: (res, root) => [
      clearCurrentEntryAction(entry.id),
      ...(root.archive ? openMoveDialog(root.archive, entry, res.data.entries) : []),
    ],
    onFailure: () => [clearCurrentEntryAction(entry.id), showGeneralError()],
  }),
];

export const openMoveDialog = (
  state: ArchiveState,
  entry: ArchiveEntry,
  destinationFolders: DestinationFolder[],
): DCEvent[] => {
  const isCustomRole = state.userRole && state.userRole.customRoleId !== "all";
  const destinations = destinationFolders.filter(
    (d) =>
      !isCustomRole ||
      (d.rootFolder && allowArchiveEdit(d.rootFolder, state.userRole)) ||
      (!d.rootFolder && allowArchiveEditForFolderType(d.type, state.userRole)),
  );
  return [setArchiveDialog(createMoveState(entry, destinations))];
};

export const selectMoveTarget = (folder: DestinationFolder): UpdateStoreEvent =>
  updateArchiveDialog("move", (d) => ({ ...d, selectedDestinationFolder: folder }));

export const setMoveConfirmChange = (confirmChange: boolean): UpdateStoreEvent =>
  updateArchiveDialog("move", (d) => ({ ...d, confirmChange }));

export const setDeleteArchiveConfirmChange = (confirmChange: boolean): UpdateStoreEvent =>
  updateArchiveDialog("confirm-delete-archive-forever", (d) => ({ ...d, confirmChange }));

export const submitMove = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "move" || !canSubmitMove(dialog)) {
    return [];
  }
  return [
    updateArchiveDialog("move", (d) => ({ ...d, isSubmitting: true })),
    archiveApiClient.move(
      state.organisationNumber,
      { entryId: dialog.entry.id, destinationId: dialog.selectedDestinationFolder.id },
      {
        onSuccess: () => [
          closeArchiveDialog(),
          setInArchiveState(
            "data",
            (archive) =>
              archive.data && {
                ...archive.data,
                entries: archive.data.entries.filter((e) => e.id !== dialog.entry.id),
              },
          ),
          showSuccess({
            key: "archive.move-success",
            params: {
              name: getEntryLabel(dialog.entry),
              destination: when(dialog.selectedDestinationFolder, getEntryLabel),
            },
          }),
          reloadArchive(),
        ],
        onFailure: () => [closeArchiveDialog(), showGeneralError()],
      },
    ),
  ];
};

export const canDragEntry = (state: ArchiveState, entry: ArchiveEntry): boolean =>
  !isArchiveListView(state) && (getEntryActions(state, entry)?.includes("move") ?? false);

export const canDropEntry = (draggedEntry: ArchiveEntry, targetEntry: ArchiveEntry): boolean =>
  Boolean(targetEntry.isFolder) &&
  targetEntry.id !== draggedEntry.id &&
  targetEntry.id !== draggedEntry.parentId &&
  !isDescendantEntry(draggedEntry, targetEntry.id);

const isDescendantEntry = (entry: ArchiveEntry, id: string): boolean =>
  (entry.children ?? []).some((child) => child.id === id || isDescendantEntry(child, id));

export const dropEntry = (
  state: ArchiveState,
  draggedEntry: ArchiveEntry,
  targetEntry: ArchiveEntry,
): DCEvent[] => {
  if (!canDragEntry(state, draggedEntry) || !canDropEntry(draggedEntry, targetEntry)) {
    return [];
  }
  return [
    actionName("archive::admin-archive::dropEntry", toJS(draggedEntry)),
    setCurrentEntryAction(draggedEntry.id, "move"),
    archiveApiClient.move(
      state.organisationNumber,
      { entryId: draggedEntry.id, destinationId: targetEntry.id },
      {
        onSuccess: () => [
          clearCurrentEntryAction(draggedEntry.id),
          setInArchiveState(
            "data",
            (archive) =>
              archive.data && {
                ...archive.data,
                entries: archive.data.entries.filter((e) => e.id !== draggedEntry.id),
              },
          ),
          showSuccess({
            key: "archive.move-success",
            params: {
              name: getEntryLabel(draggedEntry),
              destination: getEntryLabel(targetEntry),
            },
          }),
          reloadArchive(),
        ],
        onFailure: () => [clearCurrentEntryAction(draggedEntry.id), showGeneralError()],
      },
    ),
  ];
};

const PREVIEW_POLL_TIMEOUT_ID = "archive-preview-poll";

export const openPreviewDialog = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => {
  if (isPreviewing(state, entry)) {
    return [];
  }
  const file = { url: `/api${archiveEntryUrl(state, entry)}` };
  if (!hasPreviewRendition(entry)) {
    return [setArchiveDialog({ type: "preview", entry, file })];
  }
  return [
    setArchiveDialog({ type: "preview", entry, file, rendition: { status: "GENERATING" } }),
    ensurePreviewRendition(state, entry),
  ];
};

const ensurePreviewRendition = (
  state: Pick<ArchiveState, "organisationNumber">,
  entry: ArchiveEntry,
): DCEvent =>
  archiveApiClient.ensurePreview(state.organisationNumber, entry.id, {
    onSuccess: (res) => onPreviewRenditionStatus(entry, res.data),
    onFailure: () => onPreviewRenditionStatus(entry, { status: "GENERATING" }),
  });

const onPreviewRenditionStatus = (entry: ArchiveEntry, res: PreviewRendition): DCEvent[] => [
  setPreviewRenditionStatus(entry.id, res),
  ...(res.status === "GENERATING" ? [schedulePreviewRenditionPoll(entry)] : []),
];

const setPreviewRenditionStatus = (entryId: string, res: PreviewRendition): UpdateStoreEvent =>
  updateArchiveDialog("preview", (dialog) =>
    dialog.entry.id === entryId && dialog.rendition ? { ...dialog, rendition: res } : dialog,
  );

const isGeneratingPreviewOf = (dialog: ArchiveDialogState, entry: ArchiveEntry) =>
  dialog.type === "preview" &&
  dialog.entry.id === entry.id &&
  dialog.rendition?.status === "GENERATING";

const schedulePreviewRenditionPoll = (entry: ArchiveEntry): SetTimeoutEvent => ({
  type: "set-timeout",
  timeoutId: PREVIEW_POLL_TIMEOUT_ID,
  timeoutMs: PREVIEW_RENDITION_POLL_INTERVAL_MS,
  onTimeout: [
    {
      type: "with-store",
      events: (root) =>
        root.archive && isGeneratingPreviewOf(root.archive.dialog, entry)
          ? [ensurePreviewRendition(root.archive, entry)]
          : [],
    },
  ],
});

const confirmDeleteGeneralMeetingSigner = (orgNo: string, role: CompanyRole): DCEvent[] =>
  confirmAction({
    title: { key: "send-for-signing.dont-suggest-again" },
    text: {
      key: "send-for-signing.dont-suggest-general-meeting-signer.description",
      params: { name: getFullName(role) },
    },
    confirmLabel: { key: "send-for-signing.dont-suggest-again" },
    onConfirm: [
      companySettingsApi.deleteCompanyRole({
        orgNo,
        companyroleId: role.companyroleId,
        isResolving: false,
        onSuccess: () => [
          updateArchiveDialog("send-for-signing", (d) => {
            d.form.removeCompanyRole(role.companyroleId);
            return d;
          }),
          { type: "update-company-info" },
        ],
        onFailure: () => [showGeneralError()],
      }),
    ],
  });

export const openSendForSigningDialog = (
  context: ArchiveContext,
  state: ArchiveState,
  entry: ArchiveEntry,
): DCEvent[] => {
  const conversionModel = !isPdf(entry)
    ? new ConversionModel(context.http, { type: "documentId", id: entry.id }, entry.name, () => {})
    : undefined;
  const dialog: SendForSigningState = {
    type: "send-for-signing",
    entry,
    form: new SignersForm({
      signers: [signer()],
      aiClient: context.aiClient,
      user: context.user,
      shareholders: context.shareholders,
      companyRoles: context.companyInfo?.roles,
      confirmDeleteGeneralMeetingSigner: state.userRole.allowCompanyRolesEdit
        ? (role) => confirmDeleteGeneralMeetingSigner(state.organisationNumber, role)
        : undefined,
      emailBody: {
        key: "archive.send-for-signing.email-body",
        params: { senderName: getFullName(context.user), fileName: entry.name },
      },
    }),
    title: { translated: entry.name },
    conversionModel,
  };
  return [
    setArchiveDialog(dialog),
    ...(conversionModel
      ? [
          thunk((dispatch) => {
            const dispose = reaction(
              () => conversionModel.filename,
              (filename) =>
                dispatch([
                  updateArchiveDialog("send-for-signing", (d) => {
                    const emailBody = d.form.emailBody.textModels[0].model;
                    emailBody.setAndCommitInputValue(
                      emailBody.inputValue.replace(entry.name, filename),
                    );
                    return { ...d, title: { translated: filename } };
                  }),
                ]),
              { fireImmediately: false },
            );
            conversionModel?.onConvert(false);
            dispatch([
              updateArchiveDialog("send-for-signing", (d) => ({ ...d, disposeReaction: dispose })),
            ]);
          }),
        ]
      : []),
  ];
};

export const sendForSigningFilePreview = (
  state: ArchiveState,
  entry: ArchiveEntry,
): FilePreviewProps => {
  const conversionModel =
    state.dialog.type === "send-for-signing" ? state.dialog.conversionModel : undefined;

  const baseFilename = conversionModel?.filename ?? entry.name;
  const file = isOk(conversionModel?.keepDiscardResult)
    ? { data: conversionModel.keepDiscardResult.data.data }
    : isOk(conversionModel?.conversionResult)
      ? { url: `/api/convert/${conversionModel.conversionResult.data}/get` }
      : { url: `/api${archiveEntryUrl(state, entry)}` };
  const filename = isOk(conversionModel?.keepDiscardResult)
    ? conversionModel.keepDiscardResult.data.filename
    : isOk(conversionModel?.conversionResult)
      ? `${baseFilename}.pdf`
      : baseFilename;
  return {
    filename,
    file,
    width: "sm",
    conversion: conversionModel,
  };
};

export const cancelSendForSigning = (state: ArchiveState): DCEvent[] => {
  if (state.dialog.type === "send-for-signing") {
    state.dialog.disposeReaction?.();
    const model = state.dialog.conversionModel;
    if (model) {
      if (isPending(model.conversionResult)) {
        model.onCancel();
      } else if (isOk(model.conversionResult)) {
        model.onBackToOriginalFile();
      }
    }
  }
  return [closeArchiveDialog(), setInArchiveState("status", "loading"), reloadArchive()];
};

export const submitSendForSigning = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "send-for-signing" || !dialog.form.validate()) {
    return [];
  }
  const { conversionModel, entry, form } = dialog;
  const send = (entryId: string) =>
    sendForSigningEvents(state, {
      entryId,
      signers: form.toSigners(),
      emailBody: form.emailBody.textModels[0].model.inputValue,
      signingSettings: form.toSigningSettings(),
    });
  const markSending = updateArchiveDialog("send-for-signing", (d) => ({ ...d, isSending: true }));
  if (conversionModel && isOk(conversionModel.conversionResult)) {
    return [
      markSending,
      thunk((dispatch) => {
        conversionModel.onKeep();
        mobxWhen(
          () =>
            isOk(conversionModel.keepDiscardResult) || isFailure(conversionModel.keepDiscardResult),
          () => {
            const newDocumentId = isOk(conversionModel.keepDiscardResult)
              ? conversionModel.keepDiscardResult.data.newDocumentId
              : undefined;
            dispatch(send(newDocumentId ?? entry.id));
          },
        );
      }),
    ];
  }
  return [markSending, ...send(entry.id)];
};

const sendForSigningEvents = (
  state: ArchiveState,
  {
    entryId,
    signers,
    emailBody,
    signingSettings,
  }: {
    entryId: string;
    signers: Signer[];
    emailBody: string;
    signingSettings: SigningSettings;
  },
): DCEvent[] => {
  if (!state.hasRequiredSubscription) {
    return requireSubscriptionEvents();
  }
  return [
    httpPost({
      url: `/document/send-uploaded-document-for-signing/${state.organisationNumber}`,
      data: {
        documentId: entryId,
        signers: signers.map(mapToSigners),
        emailBody,
        signingSettings,
      },
      onSuccess: () => [
        closeArchiveDialog(),
        showSuccess({ key: "archive.send-for-signing-success" }, { autoHide: true }),
        setInArchiveState("status", "loading"),
        reloadArchive(),
      ],
      onFailure: () => [closeArchiveDialog(), showError({ key: "archive.send-for-signing-error" })],
    }),
  ];
};

export const openSigningStatusDialog = (state: ArchiveState, entry: ArchiveEntry): DCEvent[] => [
  thunk((dispatch) => {
    const dialogModel = new ViewDocumentSigningStatusDialogModel({
      documentId: entry.id,
      documentType: entry.type,
      title: { translated: entry.name },
      closeDialog: () => dispatch([closeArchiveDialog()]),
      getSigningStatus: () => dispatch([fetchSigningStatus(state, entry.id)]),
      resendDocumentForSigning: (signedDocumentId, data) =>
        dispatch([resendForSigningEvent(entry.id, signedDocumentId, data)]),
      downloadSignedDocument: () => dispatch(downloadEntry(state, entry)),
      confirmDeleteSignatureRequest: () =>
        dispatch(
          deleteSignatureRequestEvents(state, { entryId: entry.id, projectId: entry.projectId }),
        ),
    });
    dispatch([setArchiveDialog({ type: "signing-status", dialogModel })]);
    dialogModel.init();
  }),
];

export const fetchSigningStatus = (state: ArchiveState, entryId: string): HttpEvent =>
  httpGet({
    url: `/document/check-signing-status/${entryId}/${state.organisationNumber}`,
    onSuccess: (res: HttpResponse<GetDocumentSigningStatusResponse>, root) =>
      handleSigningStatusResult(root, entryId, ok(res.data)),
    onFailure: (_, root) =>
      handleSigningStatusResult(
        root,
        entryId,
        failure({ key: "archive.get-signing-status-error" }),
      ),
  });

export const resendForSigningEvent = (
  entryId: string,
  signedDocumentId: string,
  data: ResendDocumentForSigningData,
): HttpEvent =>
  httpPost({
    url: `/document/resend-for-signing/${signedDocumentId}`,
    data,
    onSuccess: (res: HttpResponse<GetDocumentSigningStatusResponse>, root) =>
      handleSigningStatusResult(root, entryId, ok(res.data)),
    onFailure: (_, root) =>
      handleSigningStatusResult(
        root,
        entryId,
        failure({ key: "archive.resend-for-signing-error" }),
      ),
  });

const handleSigningStatusResult = (
  root: RootStoreData,
  entryId: string,
  result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>,
): DCEvent[] => {
  if (root.archive?.dialog.type === "signing-status") {
    root.archive.dialog.dialogModel.setSigningStatusResult(result);
  }
  return isOk(result) ? [setEntrySigningStatus(entryId, result.data.documentSigningStatus)] : [];
};

export const openDeleteSignatureRequestDialog = (
  entry: ArchiveEntry,
  onConfirmCancel?: DCEvent[],
): DCEvent[] => [
  setArchiveDialog({ type: "confirm-delete-signature-request", entry, onConfirmCancel }),
];

export const handleDeleteSignatureRequest = (state: ArchiveState): DCEvent[] => {
  const dialog = state.dialog;
  if (dialog.type !== "confirm-delete-signature-request") {
    return [];
  }
  return [
    updateArchiveDialog("confirm-delete-signature-request", (d) => ({ ...d, loading: true })),
    ...deleteSignatureRequestEvents(state, {
      entryId: dialog.entry.id,
      onConfirmCancel: dialog.onConfirmCancel,
    }),
  ];
};

export const deleteSignatureRequestEvents = (
  state: ArchiveState,
  {
    entryId,
    projectId,
    onConfirmCancel,
  }: {
    entryId: string;
    projectId?: string;
    onConfirmCancel?: DCEvent[];
  },
): DCEvent[] => {
  const onSuccess = (): DCEvent[] =>
    onConfirmCancel ?? [
      closeArchiveDialog(),
      setEntrySigningStatus(entryId, DocumentSigningStatus.UNKNOWN),
      showGeneralSuccess(),
    ];
  const onFailure = (): DCEvent[] => [closeArchiveDialog(), showGeneralError()];
  return [
    projectId
      ? httpPost({
          url: `/document/reopen-locked-document/${entryId}/${state.organisationNumber}`,
          data: {},
          onSuccess,
          onFailure,
        })
      : httpPost({
          url: "/sign/cancel-signature-request",
          data: { id: entryId },
          onSuccess,
          onFailure,
        }),
  ];
};

export const handleFilesChange = (
  state: ArchiveState,
  files: File[],
  fileUploadConfig: FileUploadConfig,
): DCEvent[] => {
  const folderId = getFolderId(state);
  if (!files.length || folderId === undefined) {
    return [];
  }
  return [
    setInArchiveState("status", "uploading"),
    ...validateAndUploadEvents(state, files, folderId, fileUploadConfig),
  ];
};

export const handleDrop = (state: ArchiveState, files: File[]): DCEvent[] => {
  if (!canUploadFiles(state) || state.status === "drop-uploading") {
    return [];
  }
  const folderId = getFolderId(state);
  if (!files.length || folderId === undefined) {
    return [setInArchiveState("status", "ready")];
  }
  return [
    setInArchiveState("status", "drop-uploading"),
    ...validateAndUploadEvents(state, files, folderId, getFileUploadConfig(state)),
  ];
};

const validateAndUploadEvents = (
  state: ArchiveState,
  files: File[],
  folderId: string,
  fileUploadConfig: FileUploadConfig,
): DCEvent[] => [
  {
    type: "validate-files",
    files,
    fileUploadConfig,
    onSuccess: uploadArchiveFiles(state, files, folderId),
    onFailure: (message, error) => [
      showError(isFileUploadValidationError(error) ? message : { key: "archive.upload-error" }),
      setInArchiveState("status", "ready"),
    ],
  },
];

export const uploadArchiveFiles = (
  state: ArchiveState,
  files: File[],
  folderId: string,
): DCEvent[] => {
  if (!state.hasRequiredSubscription) {
    return requireSubscriptionEvents();
  }
  return [
    archiveApiClient.upload(state.organisationNumber, folderId, files, {
      onSuccess: () => [setInArchiveState("status", "ready"), reloadArchive()],
      onFailure: () => [
        showError({ key: "archive.upload-error" }),
        setInArchiveState("status", "ready"),
      ],
    }),
  ];
};

export const openPermissionDialog = (state: ArchiveState): DCEvent[] => {
  const folderId = rootFolderIdFromPath(state);
  if (!folderId) {
    return [];
  }
  const title = rootFolderFromPath(state)?.name ?? "";
  return [
    thunk((dispatch) =>
      dispatch([
        {
          type: "showChangePermissionDialog",
          title,
          objectId: toArchiveReference(folderId),
          moduleName: ModuleName.ARCHIVE,
          onUpdate: () => dispatch([fetchArchivePermissions(state.organisationNumber, folderId)]),
        },
      ]),
    ),
  ];
};

export type ArchiveContext = {
  aiClient: AIClient;
  alertStore: AlertMessageStore;
  appBar: AppBarData | null;
  companyInfo: CompanyInfo | null;
  http: AxiosInstance;
  shareholders: ExtendedShareholderDTO[];
  user: User | null;
};

export const performEntryAction = (
  context: ArchiveContext,
  state: ArchiveState,
  action: ArchiveEntryAction,
  entry: ArchiveEntry,
): DCEvent[] => {
  const events = (): DCEvent[] => {
    switch (action) {
      case "delete-signing-request":
        return openDeleteSignatureRequestDialog(entry);
      case "delete":
        return isSigningInProgress(entry)
          ? openDeleteSignatureRequestDialog(entry, deleteEntryEvents(state, entry.id))
          : [setCurrentEntryAction(entry.id, action), ...deleteEntryEvents(state, entry.id)];
      case "delete-forever":
        return openConfirmDeleteForeverDialog(entry);
      case "download":
        return entry.isFolder
          ? downloadFolder(state, entry.id, entry.name)
          : [setCurrentEntryAction(entry.id, action), ...downloadEntry(state, entry)];
      case "edit-in-dbot":
        return entry.projectId && entry.linkTargetId
          ? [
              navigateToProjectEvent(
                { orgNo: state.organisationNumber, projectId: entry.projectId },
                {
                  type: "project-navigation-state",
                  documentId: entry.linkTargetId,
                  overlay: true,
                  from: adminArchive({
                    orgNo: state.organisationNumber,
                    folderType: state.rootFolder,
                    folderId: getFolderId(state),
                    view: state.view,
                  }),
                },
              ),
            ]
          : [];
      case "move":
        return requestMove(state, entry);
      case "open":
        return [
          isArchiveListView(state)
            ? navigateArchive(state, { rootFolderId: entry.rootId, folderId: entry.id })
            : navigateArchive(state, { rootFolder: state.rootFolder, folderId: entry.id }),
        ];
      case "open-in-archive":
        return [navigateToContainingFolder(state, entry)];
      case "open-meeting":
        return entry.projectId
          ? [navigate(previewBoardMeetingForAdmin(state.organisationNumber, entry.projectId))]
          : [];
      case "preview":
        return isArchiveListView(state)
          ? [
              updateArchive({ previewOnLoadEntryId: entry.id }),
              navigateToContainingFolder(state, entry),
            ]
          : openPreviewDialog(state, entry);
      case "rename":
        return openRenameDialog(state, entry);
      case "restore":
        return [setCurrentEntryAction(entry.id, action), ...restoreEntry(state, entry.id)];
      case "send-for-signing":
        return openSendForSigningDialog(context, state, entry);
      case "see-signing-status":
        return openSigningStatusDialog(state, entry);
    }
  };

  return [
    actionName(`archive::admin-archive::performAction::${action}`, toJS(entry)), //
    ...events(),
  ];
};

export const toEntryMenuItem = ({
  action,
  onSelect,
}: {
  action: ArchiveEntryAction;
  onSelect: DCEvent[] | (() => void);
}): DropdownMenuItemType => ({
  type: "item",
  icon: getActionIcon(action),
  label: getActionLabel(action),
  color: getActionColor(action),
  onSelect,
});

export const toEntryContextMenu = (
  context: ArchiveContext,
  state: ArchiveState,
  entry: ArchiveEntry,
  actions: ArchiveEntryAction[],
): DropdownMenuItems =>
  actions.map((action) =>
    toEntryMenuItem({
      action,
      onSelect: [thunk(() => performEntryAction(context, state, action, entry))],
    }),
  );

export const createMoveState = (
  entry: ArchiveEntry,
  destinationFolders: DestinationFolder[],
): MoveState => ({
  type: "move",
  entry,
  destinationFolders,
  confirmChange: false,
  isSubmitting: false,
});

export const isMovingToDifferentArchive = (state: MoveState): boolean => {
  return (
    state.selectedDestinationFolder !== undefined &&
    state.entry.rootId !== state.selectedDestinationFolder?.rootFolderId
  );
};

export const canSubmitMove = (state: MoveState): state is ValidMoveState =>
  Boolean(state.selectedDestinationFolder) &&
  !state.isSubmitting &&
  (!isMovingToDifferentArchive(state) || state.confirmChange);

export const createDeleteArchiveState = (
  folderId: string,
  name: string,
): ConfirmDeleteArchiveForeverState => ({
  type: "confirm-delete-archive-forever",
  folderId,
  name,
  confirmChange: false,
  loading: false,
});

export const canSubmitDeleteArchiveForever = (state: ConfirmDeleteArchiveForeverState): boolean =>
  !state.loading && state.confirmChange;

export const createEmptyTrashState = (): ConfirmEmptyTrashState => ({
  type: "confirm-empty-trash",
  loading: false,
});

function createCreateFolderState({
  existingEntries,
  parentId,
  aiClient,
}: {
  existingEntries: string[];
  parentId: string | undefined;
  aiClient: AIClient;
}): CreateFolderState {
  return {
    type: "create-folder",
    parentId,
    name: new StringFieldModel({
      required: true,
      validator: (s) => validateFilename(existingEntries, s),
    }),
    description: new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "transactions.description" },
      aiClient,
    }),
    showDescription: new BooleanFieldModel(),
  };
}

function createRenameState({
  existingEntries,
  entryId,
  name,
}: {
  existingEntries: string[];
  entryId: string;
  name: string;
}): RenameState {
  return {
    type: "rename",
    entryId,
    name: createArchiveNameModel(existingEntries, name),
  };
}

const createArchiveNameModel = (existingEntries: string[], name: string): StringFieldModel =>
  new StringFieldModel({
    value: name,
    required: true,
    validator: (s) => validateFilename(existingEntries, s),
  });

const createArchiveDescriptionModel = (description: string | undefined): StringFieldModel =>
  new StringFieldModel({ value: description ?? "" });

const filterByPermission = (
  data: ArchiveRootFolder[],
  userRole: CustomRoleForUser,
): ArchiveRootFolder[] => {
  return data.filter((folder) => {
    switch (folder.folderType) {
      case ArchiveRootFolderType.BOARD_ARCHIVE:
        return userRole.allowBoardArchiveView;
      case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
        return userRole.allowShareholderArchiveView;
      case ArchiveRootFolderType.ADMIN_ARCHIVE:
        return userRole.allowAdminArchiveView;
      case ArchiveRootFolderType.CUSTOM_ARCHIVE:
        return userRole.allowCustomArchiveView;
      default:
        return false;
    }
  });
};

const rootFolderNames = (rootFolders: ArchiveRootFolder[]): { id: string; name: string }[] =>
  rootFolders.map((d) => ({ id: d.folderId, name: d.folderTitle ?? "" }));

export function findRootFolder(
  rootFolders: Array<ArchiveRootFolder>,
  rootFolder: ArchiveRootFolderType,
  rootFolderId?: string,
) {
  return rootFolders.find(
    (f) =>
      f.folderType === rootFolder && (rootFolderId === undefined || f.folderId === rootFolderId),
  );
}

export function validateFilename(existingNames: string[], name: string): TMessage | undefined {
  if (notUnique(existingNames, name)) {
    return { key: "archive.validation.not-unique-name" };
  }
  if (!/^[0-9A-Za-zÀ-ÖØ-öø-ÿ (),._-]+$/.test(name)) {
    return { key: "archive.validation.invalid-characters" };
  }
}

function notUnique(existingNames: string[], name: string) {
  return existingNames.some(
    (existingName) => existingName.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase(),
  );
}

export function getExistingNames(
  entries: { id?: string; name: string }[],
  currentEntryId?: string,
): string[] {
  return entries
    .filter((entry) => !currentEntryId || entry.id !== currentEntryId)
    .map((entry) => entry.name);
}

function validateCreateFolderState(state: CreateFolderState): boolean {
  state.name.validate();
  return state.name.isValid;
}

function validateRenameState(state: RenameState): boolean {
  state.name.validate();
  return state.name.isValid;
}

export function replaceEntry(entries: ArchiveEntry[], entry: ArchiveEntry): ArchiveEntry[] {
  return entries.map((e) => (e.id === entry.id ? entry : e));
}

const allowArchiveEditForFolderType = (
  rootFolder: ArchiveDocumentType,
  userRole: CustomRoleForUser,
): boolean => {
  switch (rootFolder) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return userRole.allowBoardArchiveEdit ?? false;
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return userRole.allowShareholderArchiveEdit ?? false;
    case ArchiveDocumentType.CUSTOM_ARCHIVE_FOLDER:
      return userRole.allowCustomArchiveEdit ?? false;
    case ArchiveDocumentType.ADMIN_ARCHIVE_FOLDER:
      return userRole.allowAdminArchiveEdit ?? false;
    default:
      return false;
  }
};

const allowArchiveEdit = (
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): boolean => {
  switch (rootFolder) {
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return userRole.allowBoardArchiveEdit ?? true;
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return userRole.allowShareholderArchiveEdit ?? true;
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return userRole.allowAdminArchiveEdit ?? true;
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return userRole.allowCustomArchiveEdit ?? true;
    default:
      return true;
  }
};

const folderActions = (
  entry: ArchiveEntry,
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] => {
  const allowEdit = allowArchiveEdit(rootFolder, userRole);
  return compact([
    "open",
    "download",
    allowEdit && !entry.isReadonly && "move",
    allowEdit && !entry.isReadonly && "rename",
    allowEdit && !entry.isReadonly && "delete",
  ]);
};

const isPdf = (entry: ArchiveEntry) => isExtensionPdf(entry.name);

const isConvertibleToSign = (entry: ArchiveEntry): boolean =>
  supportedFiletypesForConversion.includes(getExtension(entry.name) ?? "");

const isSentForSigning = (entry: ArchiveEntry) =>
  entry.signingStatus &&
  [
    DocumentSigningStatus.SIGNING_IN_PROGRESS,
    DocumentSigningStatus.SENT_FOR_SIGNING,
    DocumentSigningStatus.SIGNING_COMPLETE,
  ].includes(entry.signingStatus);

const fileActions = (
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] => {
  const canEdit = allowArchiveEdit(rootFolder, userRole);
  switch (entry.type) {
    case ArchiveDocumentType.LINK:
      switch (entry.linkTargetType) {
        case ArchiveDocumentType.BOARD_MEETING:
          return compact([
            userRole.allowDbot &&
              entries.some((e) => e.linkTargetType === ArchiveDocumentType.NOTICE_BOARD_MEETING) &&
              "open-meeting",
            userRole.allowDbot && "edit-in-dbot",
          ]);
        case ArchiveDocumentType.NOTICE_BOARD_MEETING:
          return compact([
            userRole.allowDbot && "open-meeting",
            userRole.allowDbot && "edit-in-dbot",
          ]);
        default:
          return ["preview", "download"];
      }
    default:
      return compact([
        "preview",
        "download",
        (isPdf(entry) || isConvertibleToSign(entry)) &&
        entry.signingStatus === DocumentSigningStatus.UNKNOWN &&
        canEdit
          ? "send-for-signing"
          : false,
        isSentForSigning(entry) && canEdit && "see-signing-status",
        canEdit && isSigningInProgress(entry) && "delete-signing-request",
        !entry.isReadonly && canEdit && "move",
        !entry.isReadonly && canEdit && "rename",
        !entry.isReadonly && canEdit && "delete",
      ]);
  }
};

function entryActionsFor(
  rootFolder: ArchiveRootFolderType,
  view: ArchiveRootFolderView,
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] {
  switch (view) {
    case "deleted":
      return compact([
        "restore",
        entry.type === ArchiveDocumentType.FILE && "preview",
        entry.type === ArchiveDocumentType.FILE && "download",
        "delete-forever",
      ]);
    default:
      return entry.isFolder
        ? folderActions(entry, rootFolder, userRole)
        : fileActions(entry, entries, rootFolder, userRole);
  }
}

function getActionColor(action: ArchiveEntryAction) {
  switch (action) {
    case "delete-forever":
      return "error";
  }
}

function primaryEntryActionFor(
  rootFolder: ArchiveRootFolderType,
  view: ArchiveRootFolderView,
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  userRole: CustomRoleForUser,
): ArchiveEntryAction | undefined {
  switch (entry.type) {
    case ArchiveDocumentType.LINK:
      switch (entry.linkTargetType) {
        case ArchiveDocumentType.BOARD_MEETING:
        case ArchiveDocumentType.NOTICE_BOARD_MEETING:
          return entryActionsFor(rootFolder, view, entry, entries, userRole)[0];
        default:
          return "preview";
      }
    default:
      return entry.isFolder ? (view === "deleted" ? undefined : "open") : "preview";
  }
}

export function getRootFolderDescription(
  rootFolder: ArchiveRootFolder,
  companyInfo: CompanyInfo | null,
): (TMessage | string)[] {
  switch (rootFolder.folderType) {
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return [{ key: "archive.shareholderCommunication.description" }];
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return [{ key: "archive.board-archive-description" }];
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return [
        {
          key: "archive.admin-archive-description",
          params: { companyName: companyInfo?.company.name },
        },
      ];
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return compact([
        rootFolder.folderDescription,
        {
          key: "archive.custom-archive-description",
          params: { count: rootFolder.numberOfPermissions },
        },
      ]);
    default:
      return [];
  }
}

export function getBreadcrumbs(
  path?: ArchivePathNode[],
): { id: string; name: TMessage }[] | undefined {
  if (!path || path.length === 1) {
    return undefined;
  }
  return path.map((p) => ({ id: p.id, name: getEntryLabel(p) }));
}

export const getOpenEntryLabel = (state: ArchiveState): TMessage | undefined => {
  if (state.dialog.type === "preview") {
    return getEntryLabel(state.dialog.entry);
  }
  const folder = last(state.data?.path);
  return folder && folder.id === getFolderId(state) ? getEntryLabel(folder) : undefined;
};

export function getRootFolderLabel(f: ArchiveRootFolderType, customLabel?: string): TMessage {
  switch (f) {
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return { key: "archive.board-archive" };
    case ArchiveRootFolderType.GENERAL_MEETINGS:
      return { key: "meetings.general-meetings" };
    case ArchiveRootFolderType.SEND_FOR_SIGNING:
      return { key: "archive.sent-for-signing" };
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return { key: "archive.admin-archive" };
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return { key: "archive.shareholder-archive" };
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return customLabel ? { translated: customLabel } : { key: "archive.custom-archive" };
    case ArchiveRootFolderType.RECENT:
      return { key: "archive.recent" };
    case ArchiveRootFolderType.STARRED:
      return { key: "archive.starred" };
  }
}

export function getRootFolderIcon(f: ArchiveRootFolderType, customIcon?: IconName): IconName {
  switch (f) {
    case ArchiveRootFolderType.BOARD_ARCHIVE:
    case ArchiveRootFolderType.GENERAL_MEETINGS:
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
    case ArchiveRootFolderType.RECENT:
      return "archive";
    case ArchiveRootFolderType.SEND_FOR_SIGNING:
      return "paper-plane";
    case ArchiveRootFolderType.STARRED:
      return "star";
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return customIcon ?? "archive";
  }
}

export function getSentForSigningTitle(parentTitle: string): TMessage {
  switch (parentTitle) {
    case "admin_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.ADMIN_ARCHIVE);
    case "board_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.BOARD_ARCHIVE);
    case "shareholder_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.SHAREHOLDER_ARCHIVE);
    default:
      return { translated: parentTitle };
  }
}

export function getEntryLabel(entry: ArchiveEntry | ArchivePathNode): TMessage {
  switch (entry.type) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.BOARD_ARCHIVE);
    case ArchiveDocumentType.BOARD_MEETINGS_FOLDER:
      return { key: "meetings.board-meetings" };
    case ArchiveDocumentType.GENERAL_MEETINGS_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.GENERAL_MEETINGS);
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.SHAREHOLDER_ARCHIVE);
    case ArchiveDocumentType.ADMIN_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.ADMIN_ARCHIVE);
    case ArchiveDocumentType.FOLDER:
      if (entry.meetingType === MeetingType.BOARD_MEETING) {
        return {
          key: "archive.board-meeting-folder-label",
          params: {
            meetingDate: { type: "date", value: entry.meetingDate },
          },
        };
      }
      return { translated: entry.name };
    case ArchiveDocumentType.LINK:
      if ("linkTargetType" in entry) {
        switch (entry.linkTargetType) {
          case ArchiveDocumentType.BOARD_MEETING:
            return { key: "archive.board_meeting" };
          case ArchiveDocumentType.NOTICE_BOARD_MEETING:
            return { key: "archive.notice_board_meeting" };
        }
      }
      return { translated: entry.name };
    default:
      return { translated: entry.name };
  }
}

export function getEntryIcon(entry: ArchiveEntry): IconProps {
  if (entry.isFolder) {
    return { name: "folder" };
  }
  if (entry.type === ArchiveDocumentType.LINK) {
    switch (entry.linkTargetType) {
      case ArchiveDocumentType.ATTACHMENT:
        return { name: "paperclip", color: "subtle" };
      case ArchiveDocumentType.BOARD_MEETING:
      case ArchiveDocumentType.NOTICE_BOARD_MEETING:
        return { name: "file-text", color: "info" };
    }
  }
  if (isPdf(entry)) {
    return { name: "file-pdf" };
  }
  return { name: "file" };
}

function restoreResponseToNavigationTarget(response: RestoreResponse): {
  rootFolder: ArchiveRootFolderType;
  folderId?: string;
} {
  const rootFolderNode = response.path[0];
  const parentFolderId =
    response.path.length > 1 ? response.path[response.path.length - 2].id : undefined;
  return { rootFolder: documentTypeToRootFolder(rootFolderNode.type), folderId: parentFolderId };
}

function documentTypeToRootFolder(type: ArchiveDocumentType): ArchiveRootFolderType {
  switch (type) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.BOARD_ARCHIVE;
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.SHAREHOLDER_ARCHIVE;
    case ArchiveDocumentType.GENERAL_MEETINGS_FOLDER:
      return ArchiveRootFolderType.GENERAL_MEETINGS;
    case ArchiveDocumentType.CUSTOM_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.CUSTOM_ARCHIVE;
    default:
      return ArchiveRootFolderType.ADMIN_ARCHIVE;
  }
}

export function canUpload({
  rootFolder,
  data,
  userRole,
  view,
}: {
  rootFolder: ArchiveRootFolderType;
  userRole: CustomRoleForUser;
  data?: ListArchiveResponse;
  view?: ArchiveRootFolderView;
}): boolean {
  if (!data || view === "deleted") {
    return false;
  }
  if (rootFolder === ArchiveRootFolderType.BOARD_ARCHIVE) {
    return userRole.allowBoardArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.SHAREHOLDER_ARCHIVE) {
    return userRole.allowShareholderArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.ADMIN_ARCHIVE) {
    return userRole.allowAdminArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.CUSTOM_ARCHIVE) {
    return userRole.allowCustomArchiveEdit ?? false;
  }
  return false;
}

export const getSigningStatus = (
  entry: ArchiveEntry,
): { label: TMessage; color: "success" | "warning" } | null => {
  switch (entry.signingStatus) {
    case DocumentSigningStatus.SIGNING_COMPLETE:
      return { label: { key: "archive.signed" }, color: "success" };
    case DocumentSigningStatus.SENT_FOR_SIGNING:
    case DocumentSigningStatus.SIGNING_IN_PROGRESS:
      return { label: { key: "archive.sent-for-signing" }, color: "warning" };
    default:
      return null;
  }
};

const archiveFileTypeFilters = ["all", "folder", "pdf", "doc", "xls"] as const;

export type ArchiveFileTypeFilter = (typeof archiveFileTypeFilters)[number];

function createFileTypeFilterModel(): RadioFieldModel<ArchiveFileTypeFilter> {
  return new RadioFieldModel({
    value: "all",
    options: [...archiveFileTypeFilters],
    idFn: identity,
    labelFn: fileTypeFilterLabel,
  });
}

export function fileTypeFilterIcon(f: ArchiveFileTypeFilter): IconName {
  switch (f) {
    case "all":
      return "list-bullets";
    case "folder":
      return "folder";
    case "pdf":
      return "file-pdf";
    case "doc":
      return "file-doc";
    case "xls":
      return "file-xls";
  }
}

function fileTypeFilterLabel(f: ArchiveFileTypeFilter): TMessage {
  switch (f) {
    case "all":
      return { key: "archive.filter.all-filetypes" };
    case "folder":
      return { key: "archive.filter.folder" };
    case "pdf":
      return { key: "common.pdf" };
    case "doc":
      return { key: "archive.filter.doc" };
    case "xls":
      return { key: "archive.filter.xls" };
  }
}

const archiveSignaturesFilters = ["all", "sent", "signed"] as const;

export type ArchiveSignaturesFilter = (typeof archiveSignaturesFilters)[number];

function createSignaturesFilterModel(): RadioFieldModel<ArchiveSignaturesFilter> {
  return new RadioFieldModel({
    value: "all",
    options: [...archiveSignaturesFilters],
    idFn: identity,
    labelFn: signaturesFilterLabel,
  });
}

export function signaturesFilterIcon(f: ArchiveSignaturesFilter): IconName {
  switch (f) {
    case "all":
      return "list-bullets";
    case "sent":
      return "paper-plane";
    case "signed":
      return "check";
  }
}

function signaturesFilterLabel(f: ArchiveSignaturesFilter): TMessage {
  switch (f) {
    case "all":
      return { key: "archive.filter.all-signatures" };
    case "sent":
      return { key: "archive.filter.sent-for-signing" };
    case "signed":
      return { key: "archive.filter.signed" };
  }
}

export function filterEntries(
  entries: ArchiveEntry[],
  inputValue: string,
  fileTypeFilter: ArchiveFileTypeFilter,
  signaturesFilter: ArchiveSignaturesFilter,
  t: TFunction,
) {
  const filterByName = orderlessPredicate<ArchiveEntry>((e) => t(getEntryLabel(e)), inputValue);
  return entries.filter(
    (e, i) =>
      filterByFileType(e, fileTypeFilter) &&
      filterBySignatures(e, signaturesFilter) &&
      filterByName(e, i),
  );
}

function filterByFileType(e: ArchiveEntry, fileTypeFilter: ArchiveFileTypeFilter) {
  switch (fileTypeFilter) {
    case "folder":
      return e.isFolder;
    case "doc":
      return e.name.endsWith(".doc") || e.name.endsWith(".docx");
    case "pdf":
      return e.name.endsWith(".pdf");
    case "xls":
      return e.name.endsWith(".xls") || e.name.endsWith(".xlsx");
    default:
      return true;
  }
}

function filterBySignatures(e: ArchiveEntry, signaturesFilter: ArchiveSignaturesFilter) {
  switch (signaturesFilter) {
    case "sent":
      return (
        e.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
        e.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS
      );
    case "signed":
      return e.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE;
    default:
      return true;
  }
}

export function getArchiveListTabs(state: ArchiveState | undefined): TabProps[] {
  if (!state) return [];
  return compact([
    {
      id: "all-archives",
      label: { key: "archive.all-archives" },
      isActive: state?.rootFolder === undefined,
      onSelect: [navigate(adminArchive({ orgNo: state?.organisationNumber }))],
    },
    {
      id: "recent",
      label: { key: "archive.recent" },
      isActive: state?.rootFolder === ArchiveRootFolderType.RECENT,
      onSelect: [
        navigate(
          adminArchive({
            orgNo: state.organisationNumber,
            folderType: ArchiveRootFolderType.RECENT,
          }),
        ),
      ],
    },
    {
      id: "starred",
      label: { key: "archive.starred" },
      isActive: state?.rootFolder === ArchiveRootFolderType.STARRED,
      onSelect: [
        navigate(
          adminArchive({
            orgNo: state.organisationNumber,
            folderType: ArchiveRootFolderType.STARRED,
          }),
        ),
      ],
    },
  ]);
}

export function getArchiveTabs(state: ArchiveState, dispatch: EventDispatcher): TabProps[] {
  const rootFolder = state.rootFolder;
  if (rootFolder === undefined) {
    return [];
  }
  return compact<TabProps>([
    {
      id: "files",
      label: { key: "archive.files" },
      isActive: state.view === "files",
      onSelect: () =>
        dispatch([
          navigateToArchiveFolder(
            state,
            { folderType: rootFolder, folderId: getFolderId(state) ?? "" },
            "files",
          ),
        ]),
    },
    {
      id: "permissions",
      label: { key: "archive.permissions" },
      isActive: state.view === "permissions",
      onSelect: () =>
        dispatch([
          navigateToArchiveFolder(
            state,
            { folderType: rootFolder, folderId: selectedRootFolder(state)?.folderId ?? "" },
            "permissions",
          ),
        ]),
    },
    isCustomArchive(state) && {
      id: "settings",
      label: { key: "archive.settings" },
      isActive: state.view === "settings",
      onSelect: () =>
        dispatch([
          navigateToArchiveFolder(
            state,
            { folderType: rootFolder, folderId: selectedRootFolder(state)?.folderId ?? "" },
            "settings",
          ),
        ]),
    },
    {
      id: "trash",
      label: { key: "archive.trash" },
      isActive: state.view === "deleted",
      onSelect: () =>
        dispatch([
          navigateToArchiveFolder(
            state,
            { folderType: rootFolder, folderId: selectedRootFolder(state)?.folderId ?? "" },
            "deleted",
          ),
        ]),
    },
  ]);
}

export function isArchiveListView(routeParams: { rootFolder?: string }): boolean {
  return (
    routeParams.rootFolder === undefined ||
    routeParams.rootFolder === ArchiveRootFolderType.RECENT ||
    routeParams.rootFolder === ArchiveRootFolderType.STARRED
  );
}

export function prepArchiveNavigator(
  state: ArchiveState,
): SelectWithNavigatorProps<ArchiveRootFolder | null> {
  const items = state.rootFolders ?? [];
  const selected = selectedRootFolder(state) ?? null;
  return {
    items,
    value: selected,
    idFn: (f) => f?.folderId ?? "",
    labelFn: (f) => (f ? getRootFolderLabel(f.folderType, f.folderTitle) : undefined),
    iconFn: (f) => (f ? getRootFolderIcon(f.folderType, f.folderIcon) : undefined),
    onSelect: (f) => (f ? [navigateToArchiveFolderInSameView(state, f)] : undefined),
    selectPrev: when(
      prev(items, (f) => f.folderId === selected?.folderId),
      (f) => () => [navigateToArchiveFolderInSameView(state, f)],
    ),
    selectNext: when(
      next(items, (f) => f.folderId === selected?.folderId),
      (f) => () => [navigateToArchiveFolderInSameView(state, f)],
    ),
  };
}

export const navigateToArchiveFolderInSameView = (
  state: ArchiveState,
  rootFolder: ArchiveRootFolder,
): NavigateEvent =>
  navigateToArchiveFolder(state, rootFolder, viewForRootFolder(state.view, rootFolder.folderType));

export const isCustomArchive = (state: ArchiveState) =>
  state.rootFolder === ArchiveRootFolderType.CUSTOM_ARCHIVE;

export const viewForRootFolder = (
  view: string | undefined,
  rootFolder: ArchiveRootFolderType,
): ArchiveRootFolderView =>
  !isArchiveRootFolderView(view) ||
  (view === "settings" && rootFolder !== ArchiveRootFolderType.CUSTOM_ARCHIVE)
    ? "files"
    : view;

export const hasFolders = (state: ArchiveState) => !isEmpty(state.data?.navigationTree?.children);
